/**
 * UI strings for monetization (gate.* wish.* mon.* inv.* gift.* skin.*): the shared parental gate,
 * the child's wish screen / jar, paywall additions, manage plan + notices, invite a family, gifts,
 * book style. `vi` must cover every `en` key. Grown-up copy addresses the parent; child copy (wish.*,
 * skin.*, mon.showGrownUp.*) never mentions money.
 */
export const en = {
  /* ---- parental gate ---- */
  'gate.title': 'For grown-ups',
  'gate.body': 'Please ask a grown-up to type the answer.',
  'gate.bodyParents': 'Reports, plans and sharing live here. Please ask a grown-up to type the answer.',
  'gate.words': '{a} plus {b}',
  'gate.wrong': 'Not quite. Here is a new one ({n} tries left).',
  'gate.locked': 'Too many tries. Please wait {s} s.',
  'gate.back': 'Go back',
  'gate.pad': 'Number pad',
  'gate.ok': 'Check',
  'gate.del': 'Delete',

  /* ---- wish screen (child) ---- */
  'wish.title': 'This one is waiting on the next page!',
  'wish.body': 'Add it to your wishes? A grown-up can see your wish jar.',
  'wish.say': 'This one is waiting on the next page! Do you want to add it to your wishes?',
  'wish.add': 'Add to my wishes ⭐',
  'wish.added': 'Into the wish jar it goes!',
  'wish.addedChip': 'In your wish jar',
  'wish.inJar': "It's in your wish jar!",
  'wish.inJarBody': 'Keep drawing. You can show your grown-up your wishes any time.',
  'wish.ask': 'Ask a grown-up',
  'wish.keepDrawing': 'Keep drawing',
  'wish.kind.node': 'Lesson',
  'wish.kind.playground': 'Drawing',
  'wish.kind.world': 'World',
  'wish.kind.story': 'Story',
  'wish.kind.skin': 'Book style',
  'wish.kind.feature': 'Surprise',
  /* grown-ups corner */
  'wish.parent.tab': 'Wishes',
  'wish.parent.title': "{name}'s wish jar",
  'wish.parent.body': '{name} made {n} wishes while drawing. Nothing is ever bought by a child.',
  'wish.parent.empty': 'When {name} taps something on a later page, it can go into this jar for you to see.',
  'wish.parent.new': 'New',
  'wish.parent.cta': "Open {name}'s wishes",
  'wish.report.line': 'This week {name} wished for: {list}',
  'wish.report.cta': 'See plans',

  /* ---- paywall additions ---- */
  'mon.benefit1': '6 worlds · 174 activities, stories and masterpieces',
  'mon.benefit2': 'Every playground drawing, tale, letter and challenge',
  'mon.benefit3': 'Family: up to 5 artists, every book style',
  'mon.heroTitle': 'Meet {creature}, drawn by {name}',
  'mon.heroSub': 'Give {name} 6 worlds to explore.',
  'mon.heroDrawnBy': '{creature} · by {name}',
  'mon.loadingPrices': 'Getting prices from the store…',
  'mon.pricesOffline': "We couldn't reach the store. Check the connection and try again.",
  'mon.priceEstimate': 'Estimated prices. The store shows the final price before you pay.',
  'mon.trialBadge': '{n} days free',
  'mon.invitedChip': 'Invited by a friend: {n}-day free trial',
  'mon.startTrial': 'Start my {n}-day free trial',
  'mon.subscribe': 'Subscribe for {price}',
  'mon.perYear': 'year',
  'mon.perMonth': 'month',
  'mon.termsTrial': 'Free for {n} days, then {price} per {per}. Renews automatically until you cancel in your store settings at least 24 hours before the end of the period. Cancel before the trial ends and you pay nothing.',
  'mon.termsRenew': '{price} per {per}. Renews automatically until you cancel in your store settings at least 24 hours before the end of the period.',
  'mon.terms': 'Terms of Use',
  'mon.privacy': 'Privacy Policy',
  'mon.loading': 'Loading…',
  'mon.timelapseHd': 'Make the full-year film in HD with Family',
  'mon.worldPreview': 'Peek at all 6 worlds',
  /* onboarding: show your grown-up */
  'mon.showGrownUp.title': 'Show your grown-up your new friend!',
  'mon.showGrownUp.sub': 'Go get a grown-up, {name}. They will love it!',
  'mon.showGrownUp.say': '{name}, show your grown-up your new friend!',
  'mon.showGrownUp.go': "Here's my grown-up",
  'mon.showGrownUp.later': 'Later',

  /* ---- manage plan + notices ---- */
  'mon.plan.tab': 'Plan',
  'mon.plan.title': 'Manage plan',
  'mon.plan.free': 'Free',
  'mon.plan.plus': 'Plus',
  'mon.plan.family': 'Family',
  'mon.plan.freeBody': 'The first pages of every world are free forever. Plans open every world, story and book style.',
  'mon.plan.trialEnds': 'Free trial until {date}',
  'mon.plan.renews': 'Renews on {date}',
  'mon.plan.ends': 'Ends on {date} (auto-renew is off)',
  'mon.plan.familyShared': 'Shared with you through Family Sharing.',
  'mon.plan.queuedGifts': '{n} gift(s) waiting: they start when the current plan ends.',
  'mon.plan.billingIssue': "We couldn't renew your plan. Please update your payment method to keep access.",
  'mon.plan.graceUntil': "We couldn't renew your plan. Access continues until {date}: please update your payment method.",
  'mon.plan.updatePayment': 'Update payment method',
  'mon.plan.manage': 'Cancel or change plan',
  'mon.plan.manageWhere': 'Manage the plan where you bought it (App Store, Google Play or the web receipt email).',
  'mon.status.trialing': 'Free trial',
  'mon.status.active': 'Active',
  'mon.status.grace': 'Payment issue',
  'mon.status.billing_issue': 'Payment issue',
  'mon.status.paused': 'Paused',
  'mon.status.canceled': 'Canceled',
  'mon.status.expired': 'Expired',
  'mon.status.pending': 'Waiting for approval',
  'mon.status.none': 'Free',
  'mon.dismiss': 'Dismiss',
  'mon.notice.trial_day5.title': 'Your free trial ends soon',
  'mon.notice.trial_day5.body': 'The trial ends on {date}. After that the plan renews at {price} unless you cancel before.',
  'mon.notice.billing_issue.title': 'Payment problem',
  'mon.notice.billing_issue.body': "We couldn't renew the plan. Update your payment method so drawing can continue.",
  'mon.notice.gift_queued.title': 'A gift is waiting',
  'mon.notice.gift_queued.body': 'Someone gave your family a year of drawing. It starts when the current plan ends.',
  'mon.notice.referral_reward.title': 'Thank you for inviting a family!',
  'mon.notice.referral_reward.body': 'A family you invited subscribed: you get {months} month(s) free.',
  'mon.notice.refund.title': 'Refund processed',
  'mon.notice.refund.body': 'A refund was processed. Your drawings and progress are always kept.',
  'mon.notice.other.title': 'News about your plan',
  'mon.notice.other.body': 'Open Manage plan for details.',

  /* ---- invite a family ---- */
  'inv.title': 'Invite a family',
  'inv.body': 'Share your link with another family: they get a {n}-day free trial, and you get 1 month free when they subscribe.',
  'inv.share': 'Share link',
  'inv.copy': 'Copy',
  'inv.copied': 'Copied!',
  'inv.shareTitle': 'Draw with us on Sketchling',
  'inv.shareText': 'We draw on paper with Sketchling. Use our link for a {n}-day free trial (code {code}):',
  'inv.stats': '{claimed} joined · {paid} subscribed · {months}/{cap} free months earned',
  'inv.offline': 'Your invite link needs the internet.',
  'inv.landing.kicker': 'An invitation',
  'inv.landing.title': "You're invited to Sketchling!",
  'inv.landing.body': 'A family who draws with Sketchling invited yours. Children learn to draw on real paper, step by step. Your family gets a {n}-day free trial of the yearly plan.',
  'inv.landing.code': 'Invitation code: {code}',
  'inv.landing.accept': 'Accept: {n} days free',
  'inv.landing.start': 'Get started',
  'inv.landing.openApp': 'Open Sketchling',
  'inv.landing.badTitle': 'This invitation cannot be used',
  'inv.landing.fine': 'For grown-ups. No payment today; the store asks before anything is charged.',
  'inv.err.invalid_code': 'The invitation link is not valid any more.',
  'inv.err.self_referral': 'This is your own invitation link: share it with another family.',
  'inv.err.already_claimed': 'An invitation was already used on this account.',
  'inv.err.already_subscribed': 'Your family already has a plan, so the invitation trial does not apply.',
  'inv.err.limit': 'This invitation has been used too many times.',
  'inv.err.offline': 'No connection. Please try again.',
  'inv.err.error': 'Something went wrong. Please try again later.',

  /* ---- gifts ---- */
  'gift.kicker': 'A gift',
  'gift.title': 'Give a year of drawing',
  'gift.titleName': 'Give {name} a year of drawing',
  'gift.shareCta': 'Give {name} a year of drawing',
  'gift.body': 'A gift for the whole family: every world, story and book style for a year. The parents redeem it in their account.',
  'gift.oneYear': '1 year {plan}',
  'gift.familyNote': 'Up to 5 children',
  'gift.plusNote': 'One child',
  'gift.buy': 'Buy the gift',
  'gift.error': "The gift couldn't be created. Nothing was charged.",
  'gift.fine': 'One-time payment, no subscription. The code works for 12 months.',
  'gift.doneTitle': 'Your gift is ready!',
  'gift.doneBody': 'Send this code to the parents of {name}. They open the link and redeem it in their account.',
  'gift.someone': 'the little artist',
  'gift.redeemLink': 'Redeem link',
  'gift.mockNote': 'Test mode: no money was charged.',
  'gift.cancelled': 'Checkout was cancelled. Nothing was charged.',
  'gift.off': 'Gifts are not available right now.',
  'gift.redeemTitle': 'Redeem a gift',
  'gift.redeemBody': 'Got a gift code? It adds the plan to this account (after any current plan ends).',
  'gift.codeLabel': 'Gift code',
  'gift.redeem': 'Redeem',
  'gift.redeemOk': 'Gift redeemed! Every world is open.',
  'gift.redeemQueued': 'Gift saved! It starts when your current plan ends. You can turn off auto-renew in your store settings.',
  'gift.signInFirst': 'Sign in (or set up your family) first, then open this link again to redeem.',
  'gift.iosWeb': 'Open this gift link in a web browser to redeem it. The plan then appears in the app.',
  'gift.err.invalid_code': "That code doesn't look right.",
  'gift.err.already_redeemed': 'This gift was already redeemed.',
  'gift.err.expired': 'This gift code has expired.',
  'gift.err.offline': 'No connection. Please try again.',
  'gift.err.error': 'Something went wrong. Please try again later.',

  /* ---- book style (skins) ---- */
  'skin.title': 'Book style',
  'skin.storybook': 'Storybook',
  'skin.sticker': 'Stickers',
  'skin.pastel': 'Pastel',
};

export const vi: Record<keyof typeof en, string> = {
  'gate.title': 'Dành cho người lớn',
  'gate.body': 'Hãy nhờ bố mẹ nhập câu trả lời nhé.',
  'gate.bodyParents': 'Báo cáo, gói và chia sẻ ở đây. Hãy nhờ bố mẹ nhập câu trả lời nhé.',
  'gate.words': '{a} cộng {b}',
  'gate.wrong': 'Chưa đúng. Thử câu mới nhé (còn {n} lần).',
  'gate.locked': 'Thử sai nhiều lần. Vui lòng chờ {s} giây.',
  'gate.back': 'Quay lại',
  'gate.pad': 'Bàn phím số',
  'gate.ok': 'Kiểm tra',
  'gate.del': 'Xoá',

  'wish.title': 'Bài này đang chờ ở trang sau!',
  'wish.body': 'Con có muốn thêm vào điều ước không? Bố mẹ sẽ thấy lọ điều ước của con.',
  'wish.say': 'Bài này đang chờ ở trang sau! Con có muốn thêm vào điều ước không?',
  'wish.add': 'Thêm vào điều ước ⭐',
  'wish.added': 'Ngôi sao bay vào lọ điều ước rồi!',
  'wish.addedChip': 'Đã ở trong lọ điều ước',
  'wish.inJar': 'Đã ở trong lọ điều ước của con!',
  'wish.inJarBody': 'Con vẽ tiếp nhé. Con có thể cho bố mẹ xem điều ước bất cứ lúc nào.',
  'wish.ask': 'Hỏi bố mẹ',
  'wish.keepDrawing': 'Vẽ tiếp',
  'wish.kind.node': 'Bài học',
  'wish.kind.playground': 'Bức vẽ',
  'wish.kind.world': 'Thế giới',
  'wish.kind.story': 'Truyện',
  'wish.kind.skin': 'Phong cách sách',
  'wish.kind.feature': 'Bất ngờ',
  'wish.parent.tab': 'Điều ước',
  'wish.parent.title': 'Lọ điều ước của {name}',
  'wish.parent.body': '{name} đã ước {n} điều trong lúc vẽ. Bé không bao giờ tự mua gì.',
  'wish.parent.empty': 'Khi {name} chạm vào một trang sau, điều đó có thể vào lọ này để bạn xem.',
  'wish.parent.new': 'Mới',
  'wish.parent.cta': 'Mở điều ước của {name}',
  'wish.report.line': 'Tuần này {name} ước: {list}',
  'wish.report.cta': 'Xem các gói',

  'mon.benefit1': '6 thế giới · 174 hoạt động, truyện và Kiệt tác',
  'mon.benefit2': 'Mọi bức vẽ sân chơi, truyện, thư và thử thách',
  'mon.benefit3': 'Gia đình: tối đa 5 hoạ sĩ, mọi phong cách sách',
  'mon.heroTitle': 'Đây là {creature}, do {name} vẽ',
  'mon.heroSub': 'Mở 6 thế giới cho {name} khám phá.',
  'mon.heroDrawnBy': '{creature} · của {name}',
  'mon.loadingPrices': 'Đang lấy giá từ cửa hàng…',
  'mon.pricesOffline': 'Không kết nối được cửa hàng. Kiểm tra mạng rồi thử lại nhé.',
  'mon.priceEstimate': 'Giá ước tính. Cửa hàng hiện giá cuối cùng trước khi bạn thanh toán.',
  'mon.trialBadge': 'Miễn phí {n} ngày',
  'mon.invitedChip': 'Được bạn bè mời: dùng thử miễn phí {n} ngày',
  'mon.startTrial': 'Bắt đầu dùng thử {n} ngày miễn phí',
  'mon.subscribe': 'Đăng ký với giá {price}',
  'mon.perYear': 'năm',
  'mon.perMonth': 'tháng',
  'mon.termsTrial': 'Miễn phí {n} ngày, sau đó {price} mỗi {per}. Tự động gia hạn cho đến khi bạn huỷ trong cài đặt cửa hàng ít nhất 24 giờ trước khi hết kỳ. Huỷ trước khi hết dùng thử thì không mất phí.',
  'mon.termsRenew': '{price} mỗi {per}. Tự động gia hạn cho đến khi bạn huỷ trong cài đặt cửa hàng ít nhất 24 giờ trước khi hết kỳ.',
  'mon.terms': 'Điều khoản sử dụng',
  'mon.privacy': 'Chính sách quyền riêng tư',
  'mon.loading': 'Đang tải…',
  'mon.timelapseHd': 'Làm phim cả năm bản HD với gói Gia đình',
  'mon.worldPreview': 'Xem trước cả 6 thế giới',
  'mon.showGrownUp.title': 'Cho bố mẹ xem bạn mới của con nhé!',
  'mon.showGrownUp.sub': 'Gọi bố mẹ lại đây nào, {name}. Bố mẹ sẽ thích lắm!',
  'mon.showGrownUp.say': '{name} ơi, cho bố mẹ xem bạn mới của con nhé!',
  'mon.showGrownUp.go': 'Bố mẹ đây rồi',
  'mon.showGrownUp.later': 'Để sau',

  'mon.plan.tab': 'Gói',
  'mon.plan.title': 'Quản lý gói',
  'mon.plan.free': 'Miễn phí',
  'mon.plan.plus': 'Plus',
  'mon.plan.family': 'Gia đình',
  'mon.plan.freeBody': 'Những trang đầu của mỗi thế giới luôn miễn phí. Các gói mở mọi thế giới, truyện và phong cách sách.',
  'mon.plan.trialEnds': 'Dùng thử miễn phí đến {date}',
  'mon.plan.renews': 'Gia hạn vào {date}',
  'mon.plan.ends': 'Kết thúc vào {date} (đã tắt tự gia hạn)',
  'mon.plan.familyShared': 'Được chia sẻ với bạn qua Chia sẻ trong gia đình.',
  'mon.plan.queuedGifts': '{n} quà đang chờ: sẽ bắt đầu khi gói hiện tại kết thúc.',
  'mon.plan.billingIssue': 'Chưa gia hạn được gói. Vui lòng cập nhật phương thức thanh toán để tiếp tục.',
  'mon.plan.graceUntil': 'Chưa gia hạn được gói. Bé vẫn dùng được đến {date}: vui lòng cập nhật phương thức thanh toán.',
  'mon.plan.updatePayment': 'Cập nhật thanh toán',
  'mon.plan.manage': 'Huỷ hoặc đổi gói',
  'mon.plan.manageWhere': 'Quản lý gói ở nơi bạn đã mua (App Store, Google Play hoặc email biên nhận web).',
  'mon.status.trialing': 'Đang dùng thử',
  'mon.status.active': 'Đang hoạt động',
  'mon.status.grace': 'Lỗi thanh toán',
  'mon.status.billing_issue': 'Lỗi thanh toán',
  'mon.status.paused': 'Tạm dừng',
  'mon.status.canceled': 'Đã huỷ',
  'mon.status.expired': 'Đã hết hạn',
  'mon.status.pending': 'Đang chờ duyệt',
  'mon.status.none': 'Miễn phí',
  'mon.dismiss': 'Ẩn',
  'mon.notice.trial_day5.title': 'Sắp hết thời gian dùng thử',
  'mon.notice.trial_day5.body': 'Dùng thử kết thúc vào {date}. Sau đó gói gia hạn với giá {price} nếu bạn không huỷ trước.',
  'mon.notice.billing_issue.title': 'Có lỗi thanh toán',
  'mon.notice.billing_issue.body': 'Chưa gia hạn được gói. Hãy cập nhật phương thức thanh toán để bé tiếp tục vẽ.',
  'mon.notice.gift_queued.title': 'Có một món quà đang chờ',
  'mon.notice.gift_queued.body': 'Có người tặng gia đình bạn một năm vẽ tranh. Quà bắt đầu khi gói hiện tại kết thúc.',
  'mon.notice.referral_reward.title': 'Cảm ơn bạn đã mời một gia đình!',
  'mon.notice.referral_reward.body': 'Gia đình bạn mời đã đăng ký: bạn được tặng {months} tháng miễn phí.',
  'mon.notice.refund.title': 'Đã hoàn tiền',
  'mon.notice.refund.body': 'Đã xử lý một khoản hoàn tiền. Tranh và tiến độ của bé luôn được giữ nguyên.',
  'mon.notice.other.title': 'Tin về gói của bạn',
  'mon.notice.other.body': 'Mở Quản lý gói để xem chi tiết.',

  'inv.title': 'Mời một gia đình',
  'inv.body': 'Chia sẻ link với một gia đình khác: họ được dùng thử miễn phí {n} ngày, bạn được 1 tháng miễn phí khi họ đăng ký.',
  'inv.share': 'Chia sẻ link',
  'inv.copy': 'Sao chép',
  'inv.copied': 'Đã sao chép!',
  'inv.shareTitle': 'Cùng vẽ với Sketchling nhé',
  'inv.shareText': 'Nhà mình đang tập vẽ trên giấy với Sketchling. Dùng link này để được dùng thử miễn phí {n} ngày (mã {code}):',
  'inv.stats': '{claimed} đã tham gia · {paid} đã đăng ký · {months}/{cap} tháng miễn phí đã nhận',
  'inv.offline': 'Link mời cần có mạng.',
  'inv.landing.kicker': 'Lời mời',
  'inv.landing.title': 'Bạn được mời dùng Sketchling!',
  'inv.landing.body': 'Một gia đình đang vẽ cùng Sketchling đã mời gia đình bạn. Bé học vẽ trên giấy thật, từng bước một. Gia đình bạn được dùng thử miễn phí {n} ngày gói năm.',
  'inv.landing.code': 'Mã mời: {code}',
  'inv.landing.accept': 'Nhận lời mời: {n} ngày miễn phí',
  'inv.landing.start': 'Bắt đầu',
  'inv.landing.openApp': 'Mở Sketchling',
  'inv.landing.badTitle': 'Không dùng được lời mời này',
  'inv.landing.fine': 'Dành cho người lớn. Hôm nay không thanh toán; cửa hàng sẽ hỏi trước khi trừ tiền.',
  'inv.err.invalid_code': 'Link mời không còn hợp lệ.',
  'inv.err.self_referral': 'Đây là link mời của chính bạn: hãy gửi cho một gia đình khác nhé.',
  'inv.err.already_claimed': 'Tài khoản này đã dùng một lời mời rồi.',
  'inv.err.already_subscribed': 'Gia đình bạn đã có gói nên không áp dụng dùng thử từ lời mời.',
  'inv.err.limit': 'Lời mời này đã được dùng quá nhiều lần.',
  'inv.err.offline': 'Không có mạng. Vui lòng thử lại.',
  'inv.err.error': 'Có lỗi xảy ra. Vui lòng thử lại sau.',

  'gift.kicker': 'Quà tặng',
  'gift.title': 'Tặng một năm vẽ tranh',
  'gift.titleName': 'Tặng {name} một năm vẽ tranh',
  'gift.shareCta': 'Tặng {name} một năm vẽ tranh',
  'gift.body': 'Món quà cho cả nhà: mọi thế giới, truyện và phong cách sách trong một năm. Bố mẹ bé nhận quà trong tài khoản của mình.',
  'gift.oneYear': '1 năm {plan}',
  'gift.familyNote': 'Tối đa 5 bé',
  'gift.plusNote': 'Một bé',
  'gift.buy': 'Mua quà',
  'gift.error': 'Chưa tạo được quà. Bạn chưa bị trừ tiền.',
  'gift.fine': 'Thanh toán một lần, không phải thuê bao. Mã dùng được trong 12 tháng.',
  'gift.doneTitle': 'Quà đã sẵn sàng!',
  'gift.doneBody': 'Gửi mã này cho bố mẹ của {name}. Họ mở link và nhận quà trong tài khoản.',
  'gift.someone': 'hoạ sĩ nhí',
  'gift.redeemLink': 'Link nhận quà',
  'gift.mockNote': 'Chế độ thử: không trừ tiền.',
  'gift.cancelled': 'Đã huỷ thanh toán. Bạn chưa bị trừ tiền.',
  'gift.off': 'Hiện chưa thể tặng quà.',
  'gift.redeemTitle': 'Nhận quà tặng',
  'gift.redeemBody': 'Bạn có mã quà? Mã sẽ thêm gói vào tài khoản này (sau khi gói hiện tại kết thúc, nếu có).',
  'gift.codeLabel': 'Mã quà',
  'gift.redeem': 'Nhận quà',
  'gift.redeemOk': 'Đã nhận quà! Mọi thế giới đã mở.',
  'gift.redeemQueued': 'Đã lưu quà! Quà bắt đầu khi gói hiện tại kết thúc. Bạn có thể tắt tự gia hạn trong cài đặt cửa hàng.',
  'gift.signInFirst': 'Hãy đăng nhập (hoặc tạo hồ sơ gia đình) trước, rồi mở lại link này để nhận quà.',
  'gift.iosWeb': 'Mở link quà này trên trình duyệt web để nhận quà. Gói sẽ hiện trong ứng dụng sau đó.',
  'gift.err.invalid_code': 'Mã này có vẻ chưa đúng.',
  'gift.err.already_redeemed': 'Quà này đã được nhận rồi.',
  'gift.err.expired': 'Mã quà đã hết hạn.',
  'gift.err.offline': 'Không có mạng. Vui lòng thử lại.',
  'gift.err.error': 'Có lỗi xảy ra. Vui lòng thử lại sau.',

  'skin.title': 'Phong cách sách',
  'skin.storybook': 'Sách truyện',
  'skin.sticker': 'Sticker',
  'skin.pastel': 'Pastel',
};
